import React, { useState, useRef, useEffect } from 'react';
import { FaChevronDown, FaCheck } from 'react-icons/fa';
import './CheckboxMultiSelect.scss';

interface Option {
  value: any;
  label: string;
}

interface CheckboxMultiSelectProps {
  options: Option[];
  value: Option[];
  onChange: (selectedOptions: Option[]) => void;
  placeholder: string;
}

const CheckboxMultiSelect: React.FC<CheckboxMultiSelectProps> = ({
  options,
  value,
  onChange,
  placeholder
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleOptionClick = (option: Option) => {
    const isSelected = value.some(item => item.value === option.value);
    let newValue: Option[];

    if (isSelected) {
      // Remove option
      newValue = value.filter(item => item.value !== option.value);
    } else {
      // Add option
      newValue = [...value, option];
    }

    onChange(newValue);
  };

  const getDisplayText = () => {
    if (value.length === 0) {
      return placeholder;
    }
    if (value.length === 1) {
      return value[0].label;
    }
    return `${value.length} seleccionados`;
  };

  return (
    <div className="receipts-checkbox-multiselect" ref={dropdownRef}>
      <div 
        className={`receipts-checkbox-multiselect__control ${isOpen ? 'receipts-checkbox-multiselect__control--is-open' : ''}`}
        onClick={() => setIsOpen(!isOpen)}
      >
        <div className="receipts-checkbox-multiselect__value-container">
          <span className={`receipts-checkbox-multiselect__placeholder ${value.length > 0 ? 'receipts-checkbox-multiselect__placeholder--has-value' : ''}`}>
            {getDisplayText()}
          </span>
        </div>
        <div className="receipts-checkbox-multiselect__indicators">
          <FaChevronDown 
            className={`receipts-checkbox-multiselect__dropdown-indicator ${isOpen ? 'receipts-checkbox-multiselect__dropdown-indicator--rotated' : ''}`}
          />
        </div>
      </div>

      {isOpen && (
        <div className="receipts-checkbox-multiselect__menu">
          <div className="receipts-checkbox-multiselect__menu-list">
            {options.map((option) => {
              const isSelected = value.some(item => item.value === option.value);
              return (
                <div
                  key={option.value}
                  className={`receipts-checkbox-multiselect__option ${isSelected ? 'receipts-checkbox-multiselect__option--is-selected' : ''}`}
                  onClick={() => handleOptionClick(option)}
                >
                  <div className="receipts-checkbox-multiselect__checkbox-container">
                    <div className={`receipts-checkbox-multiselect__checkbox ${isSelected ? 'receipts-checkbox-multiselect__checkbox--checked' : ''}`}>
                      {isSelected && <FaCheck className="receipts-checkbox-multiselect__checkbox-icon" />}
                    </div>
                    <span className="receipts-checkbox-multiselect__option-label">
                      {option.label}
                    </span>
                  </div>
                </div>
              );
            })}
            {options.length === 0 && (
              <div className="receipts-checkbox-multiselect__no-options">
                No hay opciones disponibles
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default CheckboxMultiSelect;
